import { useRef, useState } from 'react';
import { Dialog, Button } from '@/design-system';
import type { EditorGraph } from '@/features/funnel-editor/domain/editor-graph';
import { serializeEditorGraph } from '@/features/funnel-editor/persistence/serializer';
import {
  buildExportPackage,
  serializeExportPackage,
} from '@/domain/export-import/build-export-package';
import { exportFilename } from '@/domain/export-import/filename';
import type { ExportFormat, ExportScope, ImageScale } from '@/domain/export-import/contracts';
import { downloadBlob, downloadText } from './download';
import { exportPdf, exportPng } from './export-visual';
import { ExportSurface } from './ExportSurface';
import { recordFunnelExport } from '@/features/dashboard/api/dashboard-api';

type Progress = 'idle' | 'preparing' | 'rendering' | 'generating' | 'done' | 'error';
const PROGRESS_LABEL: Record<Progress, string> = {
  idle: '',
  preparing: 'Preparando',
  rendering: 'Renderizando',
  generating: 'Gerando',
  done: 'Concluído',
  error: 'Erro',
};

export function ExportDialog({
  open,
  onClose,
  funnelId,
  funnelName,
  description,
  graph,
  flush,
}: {
  open: boolean;
  onClose: () => void;
  funnelId: string;
  funnelName: string;
  description?: string | null;
  graph: EditorGraph;
  flush: () => Promise<boolean>;
}) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [format, setFormat] = useState<ExportFormat>('png');
  const [scope, setScope] = useState<ExportScope>('full_graph');
  const [scale, setScale] = useState<ImageScale>(2);
  const [transparent, setTransparent] = useState(false);
  const [progress, setProgress] = useState<Progress>('idle');
  const [message, setMessage] = useState<string | null>(null);

  async function generate() {
    setMessage(null);
    setProgress('preparing');
    if (!(await flush())) {
      setProgress('error');
      setMessage('Não foi possível salvar as últimas alterações. Tente novamente ou cancele a exportação.');
      return;
    }
    const canonical = serializeEditorGraph(graph);
    try {
      if (format === 'json') {
        const pkg = buildExportPackage({ name: funnelName, description, graph: canonical });
        setProgress('generating');
        downloadText(serializeExportPackage(pkg), exportFilename(funnelName, 'darkfunnels.json'));
      } else {
        const surface = surfaceRef.current?.firstElementChild;
        if (!(surface instanceof HTMLElement)) throw new Error('A superfície de exportação não está disponível.');
        setProgress('rendering');
        if (format === 'png') {
          const result = await exportPng(surface, { scale, transparent });
          downloadBlob(result.blob, exportFilename(funnelName, 'png'));
          if (result.warnings.length) setMessage(result.warnings.join(' '));
        } else {
          const result = await exportPdf(surface);
          downloadBlob(result.blob, exportFilename(funnelName, 'pdf'));
          const warnings = [...result.warnings];
          if (scope === 'full_graph' && graph.nodes.length > 50) {
            warnings.push('Este funil denso pode ser difícil de ler em uma única página A4.');
          }
          if (warnings.length) setMessage(warnings.join(' '));
        }
      }
      void recordFunnelExport(funnelId, format).catch(() => undefined);
      setProgress('done');
    } catch (error) {
      setProgress('error');
      setMessage(
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : `A exportação falhou${error instanceof Event ? ` durante ${error.type}` : ''}.`,
      );
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} title="Exportar funil">
        <div className="space-y-4">
          <fieldset>
            <legend className="mb-2 text-sm text-text-secondary">Formato</legend>
            <div className="flex gap-2">
              {(['png', 'pdf', 'json'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={format === value}
                  className="rounded border border-border-subtle px-3 py-2 uppercase aria-pressed:border-border-brand aria-pressed:text-text-brand"
                  onClick={() => {
                    setFormat(value);
                    if (value === 'pdf' && scope === 'current_view') setScope('full_graph');
                  }}
                >
                  {value}
                </button>
              ))}
            </div>
          </fieldset>
          {format !== 'json' ? (
            <label className="block text-sm">
              Escopo
              <select
                aria-label="Escopo da exportação"
                value={scope}
                onChange={(event) => setScope(event.target.value as ExportScope)}
                className="mt-1 w-full rounded bg-bg-app p-2"
              >
                <option value="full_graph">Funil completo</option>
                {format === 'png' ? <option value="current_view">Visualização atual</option> : null}
                <option value="presentation">Visualização da apresentação</option>
              </select>
            </label>
          ) : null}
          {format === 'png' ? (
            <div className="flex gap-4">
              <label className="text-sm">
                Resolução
                <select
                  aria-label="Resolução do PNG"
                  value={scale}
                  onChange={(event) => setScale(Number(event.target.value) as ImageScale)}
                  className="ml-2 rounded bg-bg-app p-2"
                >
                  <option value={1}>1x</option>
                  <option value={2}>2x</option>
                  <option value={3}>3x</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={transparent}
                  onChange={(event) => setTransparent(event.target.checked)}
                />
                Fundo transparente
              </label>
            </div>
          ) : null}
          <div role="status" aria-live="polite" className="min-h-5 text-xs text-text-muted">
            {PROGRESS_LABEL[progress]}
          </div>
          {message ? <p role={progress === 'error' ? 'alert' : 'status'}>{message}</p> : null}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              disabled={progress === 'preparing' || progress === 'rendering'}
              onClick={() => void generate()}
            >
              {progress === 'error' ? 'Tentar exportar novamente' : `Exportar ${format.toUpperCase()}`}
            </Button>
          </div>
        </div>
      </Dialog>
      {open ? (
        <div ref={surfaceRef} aria-hidden="true" className="fixed left-[-100000px] top-0">
          <ExportSurface
            graph={graph}
            funnelId={funnelId}
            funnelName={funnelName}
            scope={scope}
            transparent={format === 'png' && transparent}
          />
        </div>
      ) : null}
    </>
  );
}
